import React, { useState, useEffect } from "react";
import {
  Bus,
  MapPin,
  Clock,
  Users,
  Navigation,
  AlertTriangle,
  CheckCircle,
  Play,
  Pause,
  RefreshCw,
  Settings,
  Map,
  UserCheck,
  Bell,
  X,
  Plus,
  Edit,
  Trash2,
  Save,
  Search,
  Filter,
} from "lucide-react";
import "./TransportationSystem.css";

const TransportationSystem = () => {
  const [activeTab, setActiveTab] = useState("tracking");
  const [buses, setBuses] = useState([]);
  const [selectedBus, setSelectedBus] = useState(null);
  const [showAddRouteModal, setShowAddRouteModal] = useState(false);
  const [showEditRouteModal, setShowEditRouteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [newRoute, setNewRoute] = useState({
    route: "",
    driver: "",
    capacity: 45,
    stops: [],
  });
  const [students, setStudents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds

  useEffect(() => {
    // Initialize mock data
    const mockBuses = [
      {
        id: 1,
        route: "Route 1 - Downtown",
        driver: "John Smith",
        capacity: 45,
        currentStudents: 32,
        status: "on-time",
        currentLocation: "Main Street & 5th Ave",
        nextStop: "Central Park",
        eta: "5 min",
        lastUpdate: "2 min ago",
        coordinates: { lat: 40.7128, lng: -74.006 },
        isActive: true,
        stops: [
          "Bus Depot",
          "Main Street & 5th Ave",
          "Central Park",
          "Oak Street",
          "School",
        ],
        students: [
          "Emma Wilson",
          "James Smith",
          "Sarah Davis",
          "Michael Brown",
        ],
      },
      {
        id: 2,
        route: "Route 2 - Suburbs",
        driver: "Sarah Johnson",
        capacity: 50,
        currentStudents: 28,
        status: "delayed",
        currentLocation: "Highway 101",
        nextStop: "Oak Street",
        eta: "15 min",
        lastUpdate: "1 min ago",
        coordinates: { lat: 40.7589, lng: -73.9851 },
        isActive: true,
        stops: [
          "Bus Depot",
          "Highway 101",
          "Oak Street",
          "Suburb Center",
          "School",
        ],
        students: ["Alex Johnson", "Maria Garcia", "David Lee"],
      },
      {
        id: 3,
        route: "Route 3 - East Side",
        driver: "Mike Chen",
        capacity: 40,
        currentStudents: 35,
        status: "on-time",
        currentLocation: "East 42nd Street",
        nextStop: "Grand Central",
        eta: "3 min",
        lastUpdate: "30 sec ago",
        coordinates: { lat: 40.7505, lng: -73.9934 },
        isActive: true,
        stops: [
          "Bus Depot",
          "East 42nd Street",
          "Grand Central",
          "Times Square",
          "School",
        ],
        students: [
          "Lisa Wang",
          "Tom Wilson",
          "Anna Smith",
          "Chris Brown",
          "Emma Davis",
        ],
      },
      {
        id: 4,
        route: "Route 4 - West Side",
        driver: "Lisa Davis",
        capacity: 45,
        currentStudents: 0,
        status: "not-started",
        currentLocation: "Bus Depot",
        nextStop: "First Stop",
        eta: "Not started",
        lastUpdate: "5 min ago",
        coordinates: { lat: 40.7614, lng: -73.9776 },
        isActive: false,
        stops: [
          "Bus Depot",
          "West Side Hub",
          "Riverside Park",
          "Lincoln Center",
          "School",
        ],
        students: [],
      },
    ];
    setBuses(mockBuses);

    // Initialize mock students
    const mockStudents = [
      { id: 1, name: "Emma Wilson", grade: "10th", busId: 1, checkedIn: true },
      { id: 2, name: "James Smith", grade: "9th", busId: 1, checkedIn: false },
      { id: 3, name: "Sarah Davis", grade: "11th", busId: 1, checkedIn: true },
      {
        id: 4,
        name: "Michael Brown",
        grade: "12th",
        busId: 1,
        checkedIn: true,
      },
      { id: 5, name: "Alex Johnson", grade: "10th", busId: 2, checkedIn: true },
      { id: 6, name: "Maria Garcia", grade: "9th", busId: 2, checkedIn: true },
      { id: 7, name: "David Lee", grade: "11th", busId: 2, checkedIn: false },
      { id: 8, name: "Lisa Wang", grade: "12th", busId: 3, checkedIn: true },
      { id: 9, name: "Tom Wilson", grade: "10th", busId: 3, checkedIn: true },
      { id: 10, name: "Anna Smith", grade: "9th", busId: 3, checkedIn: true },
    ];
    setStudents(mockStudents);

    // Initialize mock alerts
    const mockAlerts = [
      {
        id: 1,
        type: "warning",
        title: "Route 2 Delayed",
        message:
          "Bus Route 2 is running 15 minutes behind schedule due to traffic on Highway 101.",
        timestamp: new Date(Date.now() - 5 * 60 * 1000),
        busId: 2,
        acknowledged: false,
      },
      {
        id: 2,
        type: "info",
        title: "Route 3 Arriving",
        message:
          "Bus Route 3 is approaching the school and will arrive in 3 minutes.",
        timestamp: new Date(Date.now() - 2 * 60 * 1000),
        busId: 3,
        acknowledged: false,
      },
      {
        id: 3,
        type: "success",
        title: "All Routes On Time",
        message:
          "All active bus routes are running on schedule with no delays reported.",
        timestamp: new Date(Date.now() - 10 * 60 * 1000),
        busId: null,
        acknowledged: true,
      },
    ];
    setAlerts(mockAlerts);
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      updateBusData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const updateBusData = () => {
    setBuses((prevBuses) =>
      prevBuses.map((bus) => {
        if (!bus.isActive) return bus;

        // Simulate real-time updates
        const randomChange = Math.random();
        let newStatus = bus.status;
        let newEta = bus.eta;
        let newLocation = bus.currentLocation;

        if (randomChange < 0.1) {
          // 10% chance of status change
          newStatus = bus.status === "on-time" ? "delayed" : "on-time";
          newEta =
            newStatus === "delayed"
              ? `${Math.floor(Math.random() * 20) + 5} min`
              : `${Math.floor(Math.random() * 10) + 1} min`;
        }

        return {
          ...bus,
          status: newStatus,
          eta: newEta,
          lastUpdate: "Just now",
          currentStudents: Math.max(
            0,
            Math.min(
              bus.capacity,
              bus.currentStudents + Math.floor(Math.random() * 3) - 1
            )
          ),
        };
      })
    );
  };

  const tabs = [
    { id: "tracking", label: "Live Tracking", icon: Bus },
    { id: "routes", label: "Route Management", icon: Map },
    { id: "attendance", label: "Check-in/out", icon: UserCheck },
    { id: "alerts", label: "Alerts & Notifications", icon: Bell },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "on-time":
        return "green";
      case "delayed":
        return "red";
      case "not-started":
        return "gray";
      default:
        return "blue";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "on-time":
        return <CheckCircle size={16} />;
      case "delayed":
        return <AlertTriangle size={16} />;
      case "not-started":
        return <Clock size={16} />;
      default:
        return <Bus size={16} />;
    }
  };

  const handleRefreshAll = () => {
    updateBusData();
    setShowNotificationModal(true);
  };

  const handleSendAlert = (bus) => {
    const newAlert = {
      id: Date.now(),
      type: "info",
      title: `Alert sent to ${bus.route}`,
      message: `Alert sent to driver ${bus.driver} on ${bus.route}`,
      timestamp: new Date(),
      busId: bus.id,
      acknowledged: false,
    };
    setAlerts((prev) => [newAlert, ...prev]);
    setShowNotificationModal(true);
  };

  const handleAddRoute = () => {
    setNewRoute({
      route: "",
      driver: "",
      capacity: 45,
      stops: [],
    });
    setShowAddRouteModal(true);
  };

  const handleSaveNewRoute = () => {
    if (!newRoute.route || !newRoute.driver) {
      setShowNotificationModal(true);
      return;
    }

    const route = {
      id: Date.now(),
      route: newRoute.route,
      driver: newRoute.driver,
      capacity: newRoute.capacity,
      currentStudents: 0,
      status: "not-started",
      currentLocation: "Bus Depot",
      nextStop: newRoute.stops[0] || "First Stop",
      eta: "Not started",
      lastUpdate: "Just created",
      coordinates: { lat: 40.7128, lng: -74.006 },
      isActive: false,
      stops: newRoute.stops,
      students: [],
    };

    setBuses((prev) => [...prev, route]);
    setShowAddRouteModal(false);
    setShowNotificationModal(true);
  };

  const handleEditRoute = (bus) => {
    setEditingRoute(bus);
    setShowEditRouteModal(true);
  };

  const handleSaveEditRoute = () => {
    if (!editingRoute.route || !editingRoute.driver) {
      setShowNotificationModal(true);
      return;
    }

    setBuses((prev) =>
      prev.map((bus) => (bus.id === editingRoute.id ? editingRoute : bus))
    );
    setShowEditRouteModal(false);
    setShowNotificationModal(true);
  };

  const handleDeleteRoute = (busId) => {
    setBuses((prev) => prev.filter((bus) => bus.id !== busId));
    setShowEditRouteModal(false);
    setShowNotificationModal(true);
  };

  const handleViewMap = (bus) => {
    // Simulate opening map with route visualization
    const mapUrl = `https://www.google.com/maps/dir/${bus.stops.join("/")}`;
    window.open(mapUrl, "_blank");
  };

  const handleOptimizeRoute = (bus) => {
    // Simulate route optimization
    const optimizedStops = [...bus.stops].sort(() => Math.random() - 0.5);

    setBuses((prev) =>
      prev.map((b) =>
        b.id === bus.id
          ? { ...b, stops: optimizedStops, lastUpdate: "Route optimized" }
          : b
      )
    );
    setShowNotificationModal(true);
  };

  const handleCheckInStudent = (bus) => {
    const availableStudents = students.filter(
      (s) => s.busId === bus.id && !s.checkedIn
    );
    if (availableStudents.length === 0) {
      setShowNotificationModal(true);
      return;
    }

    const student = availableStudents[0];
    setStudents((prev) =>
      prev.map((s) => (s.id === student.id ? { ...s, checkedIn: true } : s))
    );

    setBuses((prev) =>
      prev.map((b) =>
        b.id === bus.id ? { ...b, currentStudents: b.currentStudents + 1 } : b
      )
    );
    setShowNotificationModal(true);
  };

  const handleCheckOutStudent = (bus) => {
    const checkedInStudents = students.filter(
      (s) => s.busId === bus.id && s.checkedIn
    );
    if (checkedInStudents.length === 0) {
      setShowNotificationModal(true);
      return;
    }

    const student = checkedInStudents[0];
    setStudents((prev) =>
      prev.map((s) => (s.id === student.id ? { ...s, checkedIn: false } : s))
    );

    setBuses((prev) =>
      prev.map((b) =>
        b.id === bus.id
          ? { ...b, currentStudents: Math.max(0, b.currentStudents - 1) }
          : b
      )
    );
    setShowNotificationModal(true);
  };

  const handleSendNotification = () => {
    const newAlert = {
      id: Date.now(),
      type: "info",
      title: "Notification Sent",
      message: "Route 3 Arriving notification sent to parents",
      timestamp: new Date(),
      busId: 3,
      acknowledged: false,
    };
    setAlerts((prev) => [newAlert, ...prev]);
    setShowNotificationModal(true);
  };

  const handleNotifyParents = () => {
    const newAlert = {
      id: Date.now(),
      type: "warning",
      title: "Parents Notified",
      message: "Parents notified about Route 2 delay",
      timestamp: new Date(),
      busId: 2,
      acknowledged: false,
    };
    setAlerts((prev) => [newAlert, ...prev]);
    setShowNotificationModal(true);
  };

  const handleUpdateETA = () => {
    setBuses((prev) =>
      prev.map((bus) =>
        bus.id === 2
          ? { ...bus, eta: "12 min", lastUpdate: "ETA updated" }
          : bus
      )
    );
    setShowNotificationModal(true);
  };

  const handleSendAlertToDriver = (bus) => {
    const newAlert = {
      id: Date.now(),
      type: "info",
      title: "Driver Alert Sent",
      message: `Alert sent to driver ${bus.driver} on ${bus.route}`,
      timestamp: new Date(),
      busId: bus.id,
      acknowledged: false,
    };
    setAlerts((prev) => [newAlert, ...prev]);
    setSelectedBus(null);
    setShowNotificationModal(true);
  };

  const handleAddStop = () => {
    setNewRoute((prev) => ({ ...prev, stops: [...prev.stops, ""] }));
  };

  const handleRemoveStop = (index) => {
    setNewRoute((prev) => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index),
    }));
  };

  const handleStopChange = (index, value) => {
    setNewRoute((prev) => ({
      ...prev,
      stops: prev.stops.map((stop, i) => (i === index ? value : stop)),
    }));
  };

  const handleEditStopChange = (index, value) => {
    setEditingRoute((prev) => ({
      ...prev,
      stops: prev.stops.map((stop, i) => (i === index ? value : stop)),
    }));
  };

  const handleAddEditStop = () => {
    setEditingRoute((prev) => ({ ...prev, stops: [...prev.stops, ""] }));
  };

  const handleRemoveEditStop = (index) => {
    setEditingRoute((prev) => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index),
    }));
  };

  const renderLiveTracking = () => (
    <div className="tracking-section">
      <div className="tracking-header">
        <div className="tracking-controls">
          <button className="btn btn-primary" onClick={handleRefreshAll}>
            <RefreshCw size={16} />
            Refresh All
          </button>
          <button
            className="btn btn-outline"
            onClick={() => setShowSettingsModal(true)}
          >
            <Settings size={16} />
            Settings
          </button>
        </div>
        <div className="tracking-stats">
          <div className="stat-item">
            <span className="stat-number">
              {buses.filter((b) => b.isActive).length}
            </span>
            <span className="stat-label">Active Buses</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {buses.filter((b) => b.status === "delayed").length}
            </span>
            <span className="stat-label">Delayed</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {buses.reduce((sum, bus) => sum + bus.currentStudents, 0)}
            </span>
            <span className="stat-label">Total Students</span>
          </div>
        </div>
      </div>

      <div className="buses-grid">
        {buses.map((bus) => (
          <div
            key={bus.id}
            className={`bus-card status-${getStatusColor(bus.status)}`}
          >
            <div className="bus-header">
              <div className="bus-info">
                <h3>{bus.route}</h3>
                <p>Driver: {bus.driver}</p>
              </div>
              <div className="bus-status">
                <span
                  className={`status-badge status-${getStatusColor(
                    bus.status
                  )}`}
                >
                  {getStatusIcon(bus.status)}
                  {bus.status.replace("-", " ").toUpperCase()}
                </span>
              </div>
            </div>

            <div className="bus-details">
              <div className="detail-row">
                <MapPin size={16} />
                <span>Current: {bus.currentLocation}</span>
              </div>
              <div className="detail-row">
                <Navigation size={16} />
                <span>Next: {bus.nextStop}</span>
              </div>
              <div className="detail-row">
                <Clock size={16} />
                <span>ETA: {bus.eta}</span>
              </div>
              <div className="detail-row">
                <Users size={16} />
                <span>
                  {bus.currentStudents}/{bus.capacity} students
                </span>
              </div>
            </div>

            <div className="bus-actions">
              <button
                className="btn btn-sm"
                onClick={() => setSelectedBus(bus)}
              >
                View Details
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleSendAlert(bus)}
              >
                Send Alert
              </button>
            </div>

            <div className="bus-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${(bus.currentStudents / bus.capacity) * 100}%`,
                  }}
                ></div>
              </div>
              <span className="progress-text">
                {Math.round((bus.currentStudents / bus.capacity) * 100)}%
                capacity
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRouteManagement = () => (
    <div className="routes-section">
      <div className="section-header">
        <h3>Route Management</h3>
        <button className="btn btn-primary" onClick={handleAddRoute}>
          <Map size={16} />
          Add Route
        </button>
      </div>

      <div className="routes-grid">
        {buses.map((bus) => (
          <div key={bus.id} className="route-card">
            <div className="route-header">
              <h4>{bus.route}</h4>
              <span
                className={`route-status status-${getStatusColor(bus.status)}`}
              >
                {bus.status}
              </span>
            </div>
            <div className="route-details">
              <div className="route-info">
                <span>Driver: {bus.driver}</span>
                <span>Capacity: {bus.capacity}</span>
                <span>Current Load: {bus.currentStudents}</span>
              </div>
              <div className="route-stops">
                <h5>Route Stops:</h5>
                <ul>
                  <li>Bus Depot</li>
                  <li>Main Street & 5th Ave</li>
                  <li>Central Park</li>
                  <li>Oak Street</li>
                  <li>School</li>
                </ul>
              </div>
            </div>
            <div className="route-actions">
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleEditRoute(bus)}
              >
                Edit Route
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleViewMap(bus)}
              >
                View Map
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleOptimizeRoute(bus)}
              >
                Optimize
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAttendance = () => (
    <div className="attendance-section">
      <div className="section-header">
        <h3>Student Check-in/out</h3>
        <div className="attendance-filters">
          <select className="form-select">
            <option>All Buses</option>
            <option>Route 1</option>
            <option>Route 2</option>
            <option>Route 3</option>
            <option>Route 4</option>
          </select>
        </div>
      </div>

      <div className="attendance-grid">
        {buses.map((bus) => (
          <div key={bus.id} className="attendance-card">
            <div className="attendance-header">
              <h4>{bus.route}</h4>
              <span className="student-count">
                {bus.currentStudents} students
              </span>
            </div>

            <div className="attendance-actions">
              <button
                className="btn btn-success"
                onClick={() => handleCheckInStudent(bus)}
              >
                <UserCheck size={16} />
                Check-in Student
              </button>
              <button
                className="btn btn-warning"
                onClick={() => handleCheckOutStudent(bus)}
              >
                <UserCheck size={16} />
                Check-out Student
              </button>
            </div>

            <div className="attendance-list">
              <h5>Students on Bus:</h5>
              {students
                .filter((student) => student.busId === bus.id)
                .map((student) => (
                  <div key={student.id} className="activity-item">
                    <span>
                      {student.name} ({student.grade})
                    </span>
                    <span
                      className={
                        student.checkedIn ? "text-green-600" : "text-red-600"
                      }
                    >
                      {student.checkedIn ? "Checked In" : "Not Checked In"}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAlerts = () => (
    <div className="alerts-section">
      <div className="section-header">
        <h3>Alerts & Notifications</h3>
        <button
          className="btn btn-primary"
          onClick={() => {
            const newAlert = {
              id: Date.now(),
              type: "info",
              title: "Custom Alert",
              message: "Custom alert sent to all drivers",
              timestamp: new Date(),
              busId: null,
              acknowledged: false,
            };
            setAlerts((prev) => [newAlert, ...prev]);
            setShowNotificationModal(true);
          }}
        >
          <Bell size={16} />
          Send Alert
        </button>
      </div>

      <div className="alerts-grid">
        {alerts.map((alert) => (
          <div key={alert.id} className={`alert-card alert-${alert.type}`}>
            <div className="alert-header">
              {alert.type === "warning" && <AlertTriangle size={20} />}
              {alert.type === "info" && <Bell size={20} />}
              {alert.type === "success" && <CheckCircle size={20} />}
              <h4>{alert.title}</h4>
              <span className="alert-time">
                {Math.floor((Date.now() - alert.timestamp.getTime()) / 60000)}{" "}
                min ago
              </span>
            </div>
            <p>{alert.message}</p>
            {alert.type === "warning" && alert.busId === 2 && (
              <div className="alert-actions">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleNotifyParents}
                >
                  Notify Parents
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleUpdateETA}
                >
                  Update ETA
                </button>
              </div>
            )}
            {alert.type === "info" && alert.busId === 3 && (
              <div className="alert-actions">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleSendNotification}
                >
                  Send Notification
                </button>
              </div>
            )}
            {!alert.acknowledged && (
              <div className="alert-actions">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    setAlerts((prev) =>
                      prev.map((a) =>
                        a.id === alert.id ? { ...a, acknowledged: true } : a
                      )
                    );
                  }}
                >
                  Mark as Read
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="transportation-system">
      <div className="page-header">
        <h1>Transportation Management System</h1>
        <p>Real-time bus tracking, route management, and student safety</p>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "tracking" && renderLiveTracking()}
        {activeTab === "routes" && renderRouteManagement()}
        {activeTab === "attendance" && renderAttendance()}
        {activeTab === "alerts" && renderAlerts()}
      </div>

      {/* Bus Details Modal */}
      {selectedBus && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{selectedBus.route} - Details</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedBus(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="bus-detail-info">
                <div className="detail-section">
                  <h4>Driver Information</h4>
                  <p>
                    <strong>Name:</strong> {selectedBus.driver}
                  </p>
                  <p>
                    <strong>Status:</strong>
                    <span
                      className={`status-badge status-${getStatusColor(
                        selectedBus.status
                      )}`}
                    >
                      {selectedBus.status}
                    </span>
                  </p>
                </div>

                <div className="detail-section">
                  <h4>Current Status</h4>
                  <p>
                    <strong>Location:</strong> {selectedBus.currentLocation}
                  </p>
                  <p>
                    <strong>Next Stop:</strong> {selectedBus.nextStop}
                  </p>
                  <p>
                    <strong>ETA:</strong> {selectedBus.eta}
                  </p>
                  <p>
                    <strong>Last Update:</strong> {selectedBus.lastUpdate}
                  </p>
                </div>

                <div className="detail-section">
                  <h4>Capacity</h4>
                  <div className="capacity-info">
                    <span>
                      {selectedBus.currentStudents} / {selectedBus.capacity}{" "}
                      students
                    </span>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${
                            (selectedBus.currentStudents /
                              selectedBus.capacity) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Route Stops</h4>
                  <ul>
                    {selectedBus.stops?.map((stop, index) => (
                      <li key={index}>{stop}</li>
                    ))}
                  </ul>
                </div>

                <div className="detail-section">
                  <h4>Students on Bus</h4>
                  <ul>
                    {selectedBus.students?.map((student, index) => (
                      <li key={index}>{student}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setSelectedBus(null)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSendAlertToDriver(selectedBus)}
              >
                Send Alert to Driver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Route Modal */}
      {showAddRouteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Route</h3>
              <button
                className="close-btn"
                onClick={() => setShowAddRouteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">Route Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={newRoute.route}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, route: e.target.value }))
                  }
                  placeholder="e.g., Route 5 - North Side"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Driver Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={newRoute.driver}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, driver: e.target.value }))
                  }
                  placeholder="e.g., John Doe"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Capacity</label>
                <input
                  type="number"
                  className="form-input"
                  value={newRoute.capacity}
                  onChange={(e) =>
                    setNewRoute((prev) => ({
                      ...prev,
                      capacity: parseInt(e.target.value),
                    }))
                  }
                  min="1"
                  max="100"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Route Stops</label>
                {newRoute.stops.map((stop, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={stop}
                      onChange={(e) => handleStopChange(index, e.target.value)}
                      placeholder={`Stop ${index + 1}`}
                    />
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveStop(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleAddStop}
                >
                  <Plus size={16} />
                  Add Stop
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowAddRouteModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveNewRoute}>
                <Save size={16} />
                Save Route
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Route Modal */}
      {showEditRouteModal && editingRoute && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Route</h3>
              <button
                className="close-btn"
                onClick={() => setShowEditRouteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">Route Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingRoute.route}
                  onChange={(e) =>
                    setEditingRoute((prev) => ({
                      ...prev,
                      route: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Driver Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingRoute.driver}
                  onChange={(e) =>
                    setEditingRoute((prev) => ({
                      ...prev,
                      driver: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Capacity</label>
                <input
                  type="number"
                  className="form-input"
                  value={editingRoute.capacity}
                  onChange={(e) =>
                    setEditingRoute((prev) => ({
                      ...prev,
                      capacity: parseInt(e.target.value),
                    }))
                  }
                  min="1"
                  max="100"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Route Stops</label>
                {editingRoute.stops?.map((stop, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={stop}
                      onChange={(e) =>
                        handleEditStopChange(index, e.target.value)
                      }
                    />
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveEditStop(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleAddEditStop}
                >
                  <Plus size={16} />
                  Add Stop
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteRoute(editingRoute.id)}
              >
                <Trash2 size={16} />
                Delete Route
              </button>
              <button
                className="btn btn-outline"
                onClick={() => setShowEditRouteModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveEditRoute}>
                <Save size={16} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Transportation Settings</h3>
              <button
                className="close-btn"
                onClick={() => setShowSettingsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    style={{ marginRight: "0.5rem" }}
                  />
                  Enable Auto-Refresh
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Refresh Interval (seconds)</label>
                <select
                  className="form-select"
                  value={refreshInterval / 1000}
                  onChange={(e) =>
                    setRefreshInterval(parseInt(e.target.value) * 1000)
                  }
                >
                  <option value="10">10 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="60">1 minute</option>
                  <option value="300">5 minutes</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Default Bus Capacity</label>
                <input
                  type="number"
                  className="form-input"
                  defaultValue="45"
                  min="1"
                  max="100"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowSettingsModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowSettingsModal(false);
                  setShowNotificationModal(true);
                }}
              >
                <Save size={16} />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {showNotificationModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Notification</h3>
              <button
                className="close-btn"
                onClick={() => setShowNotificationModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p>Action completed successfully!</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowNotificationModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportationSystem;
